<script lang="ts" setup>
import { RoutesEnum } from '~/enums'
import { TYPE_DIVIDE } from '~/constants/app'
import type {
  CasinoProviderGame,
  SidebarItem,
  SidebarSubItem,
} from '~/types/index'
import { useScrollToTopWinner } from '~/composables/top-winner/use-scroll-to-top-winner'
import { SCROLL_DELAY_MS } from '~/constants'
import { useCasinoProvider } from '~/composables/game/use-casino-provider'
import { DESKTOP_SIDEBAR_ITEMS } from '~/resources/common'

let timeOut: ReturnType<typeof setTimeout> | null = null

const route = useRoute()
const { onScrollToTopWinner } = useScrollToTopWinner()
const authStore = useAuthStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})
const { casinoProviders } = useCasinoProvider()
const emit = defineEmits(['update:modelValue'])
const { navigate } = useNavigate()

const isShowHamburgerMenu = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const router = useRouter()

watch(
  () => router.currentRoute.value,
  () => {
    if (isShowHamburgerMenu.value) {
      isShowHamburgerMenu.value = false
    }
  },
)

const onClickMenu = (menu: SidebarItem) => {
  isShowHamburgerMenu.value = false
  navigate(menu.to, menu.loginRequired, false)
}

const onClickSubMenu = (submenu: SidebarSubItem) => {
  isShowHamburgerMenu.value = false
  if (!submenu.to) {
    return
  }
  navigate(submenu.to, submenu?.loginRequired, submenu.isNewTab)
}

useHead({
  bodyAttrs: {
    class: computed(() => {
      const bodyClass = []
      if (isShowHamburgerMenu.value) {
        bodyClass.push('ovl-open-modal')
      }
      return bodyClass.join(' ')
    }),
  },
})

const panel = ref<string | undefined>()

const togglePanel = (menu: string) => {
  panel.value = panel.value === menu ? undefined : menu
}

const displayPanel = computed({
  get() {
    return panel.value
  },
  set() {},
})

const casinoMenuProviders = computed(() =>
  casinoProviders.value.map((item: CasinoProviderGame) => ({
    title: item?.name ?? '',
    key: `${RoutesEnum.LiveCasino}/${item.slug}`,
    to: `${RoutesEnum.LiveCasino}/${item.slug}`,
  })),
)

const menuSideBarItems = computed(() => {
  return DESKTOP_SIDEBAR_ITEMS(BRAND_NAME).map((item) => {
    if (item.key === RoutesEnum.LiveCasino) {
      return {
        ...item,
        items: casinoMenuProviders.value,
      }
    }
    return item
  })
})

const handleGotoTopWinner = () => {
  authStore.setIsSidebarOpen(false)

  timeOut = setTimeout(() => {
    onScrollToTopWinner()
  }, SCROLL_DELAY_MS)
}

watch(isShowHamburgerMenu, () => {
  panel.value = undefined
})

const { BRAND_NAME, EVENT_ENABLE } = useRuntimeConfig().public

onBeforeUnmount(() => {
  if (timeOut) {
    clearTimeout(timeOut)
  }
})
</script>
<template>
  <div
    :class="[
      'hamburger-menu',
      { 'hamburger-menu--active': isShowHamburgerMenu },
    ]"
  >
    <DesktopCommonSidebarSearch />

    <VExpansionPanels
      v-if="isShowHamburgerMenu"
      v-model="displayPanel"
      variant="accordion"
    >
      <VExpansionPanel
        v-for="menu in menuSideBarItems"
        :key="menu.key"
        :value="menu.key"
      >
        <VExpansionPanelTitle
          v-if="menu.type !== TYPE_DIVIDE && menu?.items?.length"
        >
          <template #actions="{ expanded }">
            <VIcon
              :icon="expanded ? 'icon-up' : 'icon-down'"
              @click="togglePanel(menu.key)"
            />
          </template>
          <NuxtLink
            :to="menu.to"
            class="v-list-item-link"
            :class="{
              'v-list-item-link--selected': route.path === menu.key,
            }"
          >
            <VListItem
              :prepend-icon="menu.icon"
              :title="menu.title"
              :value="menu.key"
            />
          </NuxtLink>
        </VExpansionPanelTitle>
        <VExpansionPanelText v-if="menu?.items?.length">
          <a
            v-for="submenu in menu?.items"
            :key="submenu.key"
            class="v-list-item-link"
            :href="submenu.to"
            :class="{
              'v-list-item-link--selected': route.fullPath === submenu.key,
            }"
            @click.prevent="onClickSubMenu(submenu)"
          >
            <VListItem :title="submenu.title" :value="submenu.key" />
          </a>
        </VExpansionPanelText>
        <a
          v-if="!menu?.items && menu.type !== TYPE_DIVIDE"
          :href="menu.to"
          class="v-list-item-link"
          :class="{
            'v-list-item-link--selected':
              route.path != RoutesEnum.Home && menu.key?.includes(route.path),
          }"
          @click.prevent="onClickMenu(menu)"
        >
          <VListItem
            :prepend-icon="menu.icon"
            :title="menu.title"
            :value="menu.key"
          />
        </a>
        <VDivider v-if="menu.type === TYPE_DIVIDE" />
      </VExpansionPanel>
    </VExpansionPanels>

    <NuxtLink v-if="EVENT_ENABLE" :to="RoutesEnum.EventDrawingLots">
      <img
        class="img-entry-event"
        src="/images/event-drawing-lots/img-san-sieu-hu-collapse-mb.webp"
        alt="entry event"
        loading="lazy"
      />
    </NuxtLink>
    <div v-else @click="handleGotoTopWinner">
      <img
        class="img-entry-event"
        src="/images/top-winning/img-san-sieu-hu-collapse-mb.webp"
        alt="entry event"
        loading="lazy"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '~/assets/styles/components/mobile/common/hamburger-menu.scss';
</style>
